import { z } from "zod";
import { actionCategorySchema } from "./connections";
import { errorCodeSchema } from "./errors";
import { fieldShapeSchema, fieldValueSchema } from "./fields";
import { FILES_PER_MESSAGE_MAX } from "./files";
import { idSchema } from "./ids";

/**
 * The vocabulary of the Chats API: what the screens read (views), what the stream says (events),
 * what a caller may send (inputs) and the small pure helpers both sides share. The server and the
 * browser import this one file.
 *
 * Views are the only shape a chat leaves the server in, and they are strict: a stored payload, a
 * raw tool result, reasoning, usage, a manifest or error text has no field to ride in, so adding
 * one by accident is a parse failure rather than a leak. Views keep the names the web's sample
 * data uses (`apps/web/src/lib/chats/types.ts`) where the shape allows; the contract differs where
 * a server must (`ownerUserId` for who started it, ISO strings for times, an `author` object).
 *
 * Event names and fields follow AG-UI 1.0 (`@ag-ui/core` 1.0.3, read as research; it is not a
 * dependency) where AG-UI has the concept. These are deliberate differences, all for display
 * safety or because a chat's stream is already scoped to one thread:
 * - no `threadId` on run events, and `RUN_STARTED` adds our `attempt`;
 * - `RUN_FINISHED.outcome` is the bare string `success` or `interrupt` (AG-UI wraps it as
 *   `{ type }` and its interrupt carries a list of interrupts, which a chat does not have);
 * - `RUN_ERROR` carries a catalog `code` (the failed run's own, `INTERNAL_ERROR` when it has none)
 *   and no `message`, because error text never travels; the snapshot's `RunView` carries the same
 *   code beside a run that finished as failed, so a reader who arrives later is told as well;
 * - `TOOL_CALL_START` carries a display-safe `step` in place of `toolCallName`, and
 *   `TOOL_CALL_END` carries the step's settled `status`; arguments and results are not streamed.
 *   A step that waits for a person's approval is told `waiting` the same way, and is settled by a
 *   later `TOOL_CALL_END` once the approval is decided; an end names its step's message
 *   (`parentMessageId`), since a tool call ID is unique within one run only.
 */

/** Who may open a chat besides its owner: `workspace` means every member of its workspace. */
export const visibilitySchema = z.enum(["private", "workspace"]);
export type Visibility = z.infer<typeof visibilitySchema>;

/**
 * How a message is stored (`agent_messages.format`), so one function can read the text of any of
 * them. A person's message is `{ text }`, and so is a plain-text row in `scripted/1`: the
 * executor writes the row that asks a question that way, and the text of an agent that never says
 * a whole message, as some test agents do (the name is a stored value and stays). The pi runner
 * writes pi's own messages.
 */
export const MESSAGE_FORMAT = {
  user: "user/1",
  scripted: "scripted/1",
  pi: "pi/1",
} as const;

/**
 * The longest message a person may send, in characters after trimming. Counted in code points,
 * as titles and previews are, so an emoji is one character. The service enforces it and answers
 * `MESSAGE_TOO_LARGE`; the input schemas let a long text through so it reaches that answer.
 */
export const MESSAGE_MAX_CHARS = 20_000;
/** The most characters of a message a preview keeps before its ellipsis. */
export const PREVIEW_MAX_CHARS = 160;
/** How many recently opened chats `recent` returns. */
export const RECENT_LIMIT = 4;
/** The page `list` returns when the caller names none, and the most it will return. */
export const LIST_DEFAULT_LIMIT = 30;
export const LIST_MAX_LIMIT = 50;
/** The newest messages a snapshot holds; earlier ones come a page at a time. */
export const SNAPSHOT_MESSAGE_LIMIT = 50;

/** The longest note the agent may write, in characters. */
export const NOTE_MAX_CHARS = 280;
/** The most notes one run may write. */
export const NOTES_PER_RUN = 3;

/** The most items one plan may hold. */
export const PLAN_ITEMS_MAX = 8;
/** The longest plan item the agent may write, in characters. */
export const PLAN_ITEM_MAX_CHARS = 80;
/** The most steps one `process.changed` names: a batch holds at most 100 edits. */
export const PROCESS_CHANGED_STEPS_MAX = 100;
/** The most plans (a first and its updates) one run may write. */
export const PLANS_PER_RUN = 20;

/** What a code run's line may hold, in characters (the sandbox work shown in the trace). */
export const WORK_PURPOSE_MAX_CHARS = 120;
export const WORK_CODE_MAX_CHARS = 20_000;
export const WORK_PRINTED_MAX_CHARS = 4_000;
export const WORK_ERRORS_MAX_CHARS = 2_000;

/** A title keeps at most this many characters before its ellipsis, cut at a word if it can. */
const TITLE_MAX_CHARS = 48;
/** A title is not cut at a word before this many characters; shorter than that, a word is split. */
const TITLE_MIN_CUT = 24;

// Helpers --------------------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** U+FFFD, written as a code so no tool turns the escape into an invisible-looking character. */
const REPLACEMENT = String.fromCharCode(0xfffd);

/** A UTF-16 surrogate with no partner: what is left of an emoji cut in half. */
const LONE_SURROGATE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;

/** Runs of whitespace, newlines included, become one space: a title and a preview are one line. */
const oneLine = (text: string) => text.replace(/\s+/g, " ").trim();

/**
 * The text a person would read in a stored message, or null when the format is unknown or the
 * payload is not the shape the format promises. The one place the formats are known, so search
 * text, previews and views all agree.
 *
 * A pi message's `content` is a string or a list of parts; only the `text` parts count and the
 * other kinds (thinking, tool calls, images) are skipped. Text parts are joined with a newline,
 * so what an assistant said before and after a tool call stays apart. A pi message with no text
 * at all is an empty string, not null: it is well formed, it just says nothing.
 */
export function messageText(format: string, payload: unknown): string | null {
  if (format === MESSAGE_FORMAT.user || format === MESSAGE_FORMAT.scripted) {
    return isRecord(payload) && typeof payload["text"] === "string" ? payload["text"] : null;
  }
  if (format === MESSAGE_FORMAT.pi) {
    if (!isRecord(payload)) return null;
    const content = payload["content"];
    if (typeof content === "string") return content;
    if (!Array.isArray(content)) return null;
    const texts: string[] = [];
    for (const part of content as unknown[]) {
      const text = isRecord(part) && part["type"] === "text" ? part["text"] : undefined;
      if (typeof text === "string" && text !== "") texts.push(text);
    }
    return texts.join("\n");
  }
  return null;
}

/**
 * A person's message ready to store: NUL characters removed (neither a text nor a jsonb column can
 * hold one), a lone surrogate replaced with U+FFFD (jsonb refuses it as well), then trimmed. Null
 * when nothing is left. It does not check the length; the service compares against
 * `MESSAGE_MAX_CHARS`.
 */
export function normalizeMessageText(text: string): string | null {
  const clean = text.replaceAll("\u0000", "").replace(LONE_SURROGATE, REPLACEMENT).trim();
  return clean === "" ? null : clean;
}

/**
 * A chat's title until Abacor writes one: its first words on one line. Up to 48 characters it is
 * the text itself; longer, it is cut at a word and ends with an ellipsis. A word is split only
 * when there is no space at or after the 24th character to cut at. Characters are code points, so
 * a cut never lands inside an emoji (a joined emoji sequence can still come apart).
 */
export function titleFrom(text: string): string {
  const chars = Array.from(oneLine(text));
  if (chars.length <= TITLE_MAX_CHARS) return chars.join("");
  // A space at index i ends a whole-word prefix of i characters, so index 48 counts too.
  const space = chars.lastIndexOf(" ", TITLE_MAX_CHARS);
  const cut = space >= TITLE_MIN_CUT ? space : TITLE_MAX_CHARS;
  return `${chars.slice(0, cut).join("")}…`;
}

/**
 * A message shortened for the list: one line, at most `PREVIEW_MAX_CHARS` characters (code
 * points), then an ellipsis when something was left out.
 */
export function previewOf(text: string): string {
  // A cite's label is a key, which is no words of a preview.
  const chars = Array.from(oneLine(text.replace(citeDirective(), "")));
  if (chars.length <= PREVIEW_MAX_CHARS) return chars.join("");
  return `${chars.slice(0, PREVIEW_MAX_CHARS).join("").trimEnd()}…`;
}

// Shared pieces --------------------------------------------------------------------------------

/** When something happened, as an ISO 8601 UTC time (`Date#toISOString`). */
const instantSchema = z.iso.datetime();
/** A position in a chat's log: messages and events share one numbering. 0 is before the first. */
const seqSchema = z.int().nonnegative();
/**
 * How many times a chat's visibility has changed: 0 until it is first shared. It fits the
 * database's `integer`.
 */
const visibilityRevisionSchema = z.int().min(0).max(2_147_483_647);
/**
 * The auth service's ID for a person. Not a TypeID check: regional tokens carry it as an opaque
 * string (see the package README), so it is only bounded.
 */
const userIdSchema = z.string().min(1).max(255);
/** What a caller invents to make a repeat of its call harmless: a send key or an answer key. */
const callerKeySchema = z.string().min(1).max(200);
/** A tool call's ID is the runner's own, not a TypeID. */
const toolCallIdSchema = z.string().min(1);

export const deliverySchema = z.enum(["steer", "followUp"]);
export type Delivery = z.infer<typeof deliverySchema>;

/**
 * Where a step stands. `waiting`: a connection tool's call waits for the person's approval and has
 * not run (agent runtime 12a); it is not over, and a later `TOOL_CALL_END` settles it.
 */
/**
 * How a step stands: still running, waiting on a person's approval, done, failed, `declined` (the
 * person said no to the approval, so nothing ran: a decision, not an error) or unknown (it may or
 * may not have run).
 */
export const stepStatusSchema = z.enum([
  "running",
  "waiting",
  "done",
  "failed",
  "declined",
  "unknown",
]);
export type StepStatus = z.infer<typeof stepStatusSchema>;

export const messageStatusSchema = z.enum(["streaming", "complete", "interrupted"]);
export type MessageStatus = z.infer<typeof messageStatusSchema>;

export const runStatusSchema = z.enum(["queued", "running", "finished"]);
export type RunStatus = z.infer<typeof runStatusSchema>;

/** The kinds of request a person can be asked for. Only `question` and `decision` are answered in a chat. */
export const requestKindSchema = z.enum(["question", "approval", "decision", "auth"]);
export type RequestKind = z.infer<typeof requestKindSchema>;

export const requestStatusSchema = z.enum(["pending", "answered", "expired", "canceled"]);
export type RequestStatus = z.infer<typeof requestStatusSchema>;

/** The caller's part in a chat: its owner, or a member it is shared with (read only). */
export const viewerSchema = z.enum(["owner", "viewer"]);
export type Viewer = z.infer<typeof viewerSchema>;

export const listScopeSchema = z.enum(["all", "yours", "needs-you"]);
export type ListScope = z.infer<typeof listScopeSchema>;

// Views ----------------------------------------------------------------------------------------

/** One line of what Abacor did: the connection as people name it and a display-safe sentence. */
export const stepViewSchema = z.strictObject({
  id: toolCallIdSchema,
  at: instantSchema,
  /** The connection, as people name it: "Gmail", "Xero". */
  app: z.string(),
  /** The one display-safe line: "Looked at Gmail". */
  text: z.string(),
  status: stepStatusSchema,
  access: actionCategorySchema,
});
export type StepView = z.infer<typeof stepViewSchema>;

/**
 * A line the agent chose to say about what it is doing, through its `note` tool: plain text, shown
 * among the steps. It is never the agent's reasoning. Its ID is its tool call's ID.
 */
export const noteViewSchema = z.strictObject({
  id: toolCallIdSchema,
  at: instantSchema,
  text: z.string().min(1).max(NOTE_MAX_CHARS),
});
export type NoteView = z.infer<typeof noteViewSchema>;

/** Where one item of a plan stands. */
export const planItemStatusSchema = z.enum(["pending", "active", "done"]);

/** One short step of a plan, in the agent's words. */
export const planItemSchema = z.strictObject({
  text: z.string().min(1).max(PLAN_ITEM_MAX_CHARS),
  status: planItemStatusSchema,
  /**
   * When the item first stood as active or done, which tells a block of work which item it was
   * working on (`planItemAt`). Worked out by whoever holds the plan (`planWithStarts`), never
   * written by the agent; absent while the item is pending, and from an older server.
   */
  startedAt: instantSchema.optional(),
});
export type PlanItem = z.infer<typeof planItemSchema>;

/**
 * The checklist the agent wrote for a turn, through its `plan` tool, as it stands now: every
 * update carries the whole list. It does not limit how many items are `active`; that rule belongs
 * to the tool.
 */
export const planViewSchema = z.strictObject({
  at: instantSchema,
  items: z.array(planItemSchema).min(1).max(PLAN_ITEMS_MAX),
});
export type PlanView = z.infer<typeof planViewSchema>;

/**
 * `next`, the whole plan as the agent just gave it, with each item's `startedAt`: kept from
 * `previous` when the item in the same place has the same words, otherwise the time of `next` for an
 * item that is active or done, and none for one still pending. The agent rewrites the whole list
 * each time, so this is how the plan remembers when it moved on. Pure, and the same for a repeat.
 */
export function planWithStarts(previous: PlanView | undefined, next: PlanView): PlanView {
  return {
    at: next.at,
    items: next.items.map(({ text, status }, index) => {
      if (status === "pending") return { text, status };
      const before = previous?.items[index];
      const startedAt =
        before?.text === text && before.startedAt !== undefined ? before.startedAt : next.at;
      return { text, status, startedAt };
    }),
  };
}

/**
 * The plan item a block of work that began at `at` was working on: the last item that had started
 * by then. The agent works down its list, so a later item has started later. Undefined before the
 * plan's first item started, or for a plan with no start times.
 */
export function planItemAt(
  plan: { readonly items: readonly PlanItem[] },
  at: Date,
): PlanItem | undefined {
  return plan.items.findLast(
    (item) => item.startedAt !== undefined && new Date(item.startedAt) <= at,
  );
}

/**
 * What a source is called in an answer: `s1`, `s2`, ... in the order the chat read them. The number
 * counts the whole chat, not one run or one stored message: an answer that took several calls is
 * stored as several messages, and a follow-up answers from what an earlier run read, so a key names
 * the same read for as long as the chat lasts. (Chats kept from before this restarted at `s1` in each
 * run; the page tells those runs apart by the repeated key.)
 */
export const SOURCE_KEY = /^s[1-9][0-9]*$/;
export const sourceKeySchema = z.string().regex(SOURCE_KEY);
/** The number in a source's key: `s12` is 12. The key must be valid (`SOURCE_KEY`). */
export const sourceNumberOf = (key: string): number => Number(key.slice(1));

/**
 * The `cite` directive as the stored text and the page read it (`:cite[s1]`, with an optional
 * `{quote="..."}` block): the spaces before it, its label and its attributes, where a quoted
 * attribute may hold a `}`. A `{` after the label that is no such block (a quotation mark that does
 * not pair, a passage with no `quote=`) is read up to its first `}` on the line, so the whole of it
 * can be taken out rather than left in the words. Never `::cite`, a leaf directive that is no cite. A new expression each
 * call, since a global one keeps its place between uses.
 */
export const citeDirective = (): RegExp =>
  /([ \t]*)(?<!:):cite\[([^\]\n]*)\](\{(?:"[^"\n]*"|'[^'\n]*'|[^}\n"'])*\}|\{[^\n]*?\})?/g;

/** The one block a cite may carry: a single quote, in either kind of quotation marks or bare. */
const WELL_FORMED_QUOTE = /^\{quote=(?:"[^"\n]*"|'[^'\n]*'|[^\s}"']+)\}$/;

/**
 * `text` with every cite's block taken out unless it is exactly one `quote="..."`. The `cite`
 * directive takes no other attribute, and a block of anything else (the passage itself with no
 * `quote=`, a second attribute) is not one a page can read: it would print the braces and what is
 * in them as the answer's words. Answers stored before the server checked this can hold such a block.
 */
export function tidyCites(text: string): string {
  return text.replace(
    citeDirective(),
    (whole: string, space: string, label: string, block?: string) =>
      block === undefined || WELL_FORMED_QUOTE.test(block) ? whole : `${space}:cite[${label}]`,
  );
}
/** The longest passage a source can quote, in characters. */
export const SOURCE_QUOTE_MAX_CHARS = 200;

/**
 * Something a run read, which its answer can cite (chat trace design, section 4). Stored nowhere on
 * its own: a `file` and a `step` source are the successful result of a `read_file` or a `look`
 * `use_tool` call, read back from the transcript, a `recording` source is the successful result of
 * a `read_recording` or `view_screen` call (a recording of the same chat, ENG-1028: the whole of it,
 * a stretch, or one screen), and a `chat` source is an
 * earlier chat brought in with `@` for one reply. `at` is the moment of the read.
 */
export const sourceViewSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    key: sourceKeySchema,
    kind: z.literal("file"),
    at: instantSchema,
    fileId: idSchema("file"),
    /** The page of the file, when it has pages. */
    page: z.int().optional(),
    quote: z.string().max(SOURCE_QUOTE_MAX_CHARS).optional(),
  }),
  z.strictObject({
    key: sourceKeySchema,
    kind: z.literal("step"),
    at: instantSchema,
    stepId: toolCallIdSchema,
  }),
  z.strictObject({
    key: sourceKeySchema,
    kind: z.literal("recording"),
    at: instantSchema,
    recordingId: idSchema("recording"),
    /** The stretch read, in milliseconds from the recording's Start; absent for its outline. */
    from: z.int().nonnegative().optional(),
    to: z.int().nonnegative().optional(),
    /** The screen looked at, kept at `from`. */
    screenId: idSchema("recordingScreen").optional(),
  }),
  z.strictObject({
    key: sourceKeySchema,
    kind: z.literal("chat"),
    at: instantSchema,
    threadId: idSchema("thread"),
  }),
]);
export type SourceView = z.infer<typeof sourceViewSchema>;

export const messageAuthorSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("person"), userId: userIdSchema }),
  z.strictObject({ kind: z.literal("assistant") }),
]);
export type MessageAuthor = z.infer<typeof messageAuthorSchema>;

/**
 * A file a message carries, as it was when the message was written: one a person sent, or one
 * Abacor made for them. Files never change, so the snapshot never goes stale; the bytes are read
 * through the Files API, never from here.
 */
export const chatFileSchema = z.strictObject({
  id: idSchema("file"),
  name: z.string().min(1).max(255),
  mediaType: z.string().min(1),
  size: z.int().min(0),
});
export type ChatFile = z.infer<typeof chatFileSchema>;

/**
 * One thing the agent did with a file or in the sandbox (chat trace, sandbox work): a `read_file`
 * call (`read`) or a `run_code` call (`run`), or an `edit_draft` call the server refused or that
 * failed (`edit`): a draft edit that landed is no line of work, since the change card shows it
 * (decision `2026-10-30-process-authored-from-the-chat`). Stored nowhere on its own: the view derives it from
 * the call and its result in the transcript (`workOf`), and the live event (`work.set`) is made
 * from the same two, so a reload shows what the stream did. `running` and `unknown` come from the
 * call alone (no result yet: the call is under way, or its run ended before the result came).
 *
 * What the model wrote (`purpose`, `code`) and what the sandbox gave back (`printed`, `errors`)
 * are data, shown as text and never as Markdown.
 */
export const workViewSchema = z.strictObject({
  id: toolCallIdSchema,
  at: instantSchema,
  kind: z.enum(["read", "run", "edit"]),
  status: stepStatusSchema,
  /** `read`: the file read. */
  fileId: idSchema("file").optional(),
  /** `run`: what the run was for, in the agent's few plain words. */
  purpose: z.string().min(1).max(WORK_PURPOSE_MAX_CHARS).optional(),
  /** `run`: the Python it ran. */
  code: z.string().min(1).max(WORK_CODE_MAX_CHARS).optional(),
  /** `run`: the files this call first put in the sandbox. */
  opened: z.array(idSchema("file")).max(10).optional(),
  /** `run`: what it printed and what it complained of, cut. */
  printed: z.string().max(WORK_PRINTED_MAX_CHARS).optional(),
  errors: z.string().max(WORK_ERRORS_MAX_CHARS).optional(),
  /** `run`: the code's own output was longer than the sandbox returns. */
  cut: z.boolean().optional(),
  exitCode: z.int().nullable().optional(),
  timedOut: z.boolean().optional(),
  /** How long the call took, in milliseconds. */
  ms: z.int().min(0).optional(),
  /** `run`: the files it made for the person. */
  made: z.array(chatFileSchema).max(FILES_PER_MESSAGE_MAX).optional(),
});
export type WorkView = z.infer<typeof workViewSchema>;

/**
 * One batch Abacor landed in a process's draft in a turn (decision
 * `2026-10-30-process-authored-from-the-chat`): the process and draft, the batch, the revision it
 * made and the steps it touched, in order. What the reply's change card is drawn from; the live
 * `process.changed` event says the same with the message it belongs to.
 */
export const processChangeViewSchema = z.strictObject({
  processId: idSchema("process"),
  draftId: idSchema("processDraft"),
  batchId: idSchema("processChange"),
  revision: z.int().min(1),
  stepIds: z.array(idSchema("processStep")).max(PROCESS_CHANGED_STEPS_MAX),
  /**
   * The change group (one whole reply) the batch went in, so the card offers Undo only while it is
   * the newest change; absent on a batch landed before groups were kept.
   */
  groupId: idSchema("playbookChangeGroup").optional(),
});
export type ProcessChangeView = z.infer<typeof processChangeViewSchema>;

/**
 * A try of a playbook's draft that Abacor started or ran again in this turn (decision
 * `2026-11-03-tries-on-the-case-runner-build-plan`, item 4): the case it is, read live by the try
 * card, which is drawn once, on the turn that first names the case, and kept current there.
 */
export const chatTryViewSchema = z.strictObject({
  caseId: idSchema("case"),
  processId: idSchema("process"),
});
export type ChatTryView = z.infer<typeof chatTryViewSchema>;

/** The most lines one table's change says, and rows one line names: a batch holds 100 edits. */
export const TABLE_CHANGE_LINES_MAX = 100;

/** The most characters one table line's words hold, table name included. */
export const TABLE_CHANGE_LINE_TEXT_MAX = 500;

/** What a table line says, so a card can draw its glyph (board SV22). */
export const TABLE_CHANGE_LINE_KINDS = [
  "made",
  "renamed",
  "column_added",
  "column_renamed",
  "column_retyped",
  "keys_set",
  "rows_added",
  "row_changed",
  "rows_removed",
] as const;

/**
 * One line of a table's change on the reply's card (decision
 * `2026-11-02-abacor-builds-a-playbook-from-one-concept-map`, item 12, board SV22), in plain words
 * with the table's name ("Carriers · added 2 rows", "Carriers · Redline's cutoff 2 pm → 3 pm").
 * `rows` are the rows the line wrote, each at the revision it left: the line's Undo reverses each
 * (`undoRow`) while every one is still at that revision. Table and column lines name no rows.
 */
export const tableChangeLineSchema = z.strictObject({
  kind: z.enum(TABLE_CHANGE_LINE_KINDS),
  text: z.string().min(1).max(TABLE_CHANGE_LINE_TEXT_MAX),
  rows: z
    .array(z.strictObject({ rowId: idSchema("dataTableRow"), revision: z.int().min(1) }))
    .max(TABLE_CHANGE_LINES_MAX),
});
export type TableChangeLine = z.infer<typeof tableChangeLineSchema>;

/**
 * What one `edit_table` batch did to one table, for the reply's card: the table, whether the batch
 * made it, its row count after, and its lines in the order they landed. Table edits save to the
 * workspace's table at once; they are no change to any draft (concept map item 8).
 */
export const tableChangeViewSchema = z.strictObject({
  /** The `edit_table` call that landed it; with `tableId`, what makes it one in its message. */
  callId: z.string().min(1).max(200),
  tableId: idSchema("dataTable"),
  /** The table's name after the batch. */
  table: z.string().min(1).max(100),
  made: z.boolean(),
  /** How many rows the table has after the batch; not a count when `rowsUnknown`. */
  rows: z.int().min(0),
  /**
   * Set when the count couldn't be read after the batch (absent otherwise): `rows` then holds
   * only what the batch saw, so draw no count. The lines and their rows are still confirmed.
   */
  rowsUnknown: z.literal(true).optional(),
  lines: z.array(tableChangeLineSchema).min(1).max(TABLE_CHANGE_LINES_MAX),
});
export type TableChangeView = z.infer<typeof tableChangeViewSchema>;

/** The part of a work line that only the tool's result can say: what `details.work` holds. */
export const workDetailsSchema = workViewSchema.pick({
  opened: true,
  printed: true,
  errors: true,
  cut: true,
  exitCode: true,
  timedOut: true,
  ms: true,
  made: true,
});
export type WorkDetails = z.infer<typeof workDetailsSchema>;

/**
 * The most things one message may mention with `@`. A mention points the agent at one file of the
 * chat or one of the person's connections; it grants nothing (a connection's tools are still
 * admitted by the gateway, call by call).
 */
export const MENTIONS_PER_MESSAGE_MAX = 10;

/** What can be mentioned in V1: a file already in the chat, or a connection the person can see. */
export const mentionKindSchema = z.enum(["file", "connection"]);
export type MentionKind = z.infer<typeof mentionKindSchema>;

/** A mention as it is sent: the kind and the thing's ID, nothing the client could misname. */
export const mentionInputSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("file"), id: idSchema("file") }),
  z.strictObject({ kind: z.literal("connection"), id: idSchema("connection") }),
]);
export type MentionInput = z.infer<typeof mentionInputSchema>;

/**
 * A mention as a message shows it: the label is the server's snapshot taken when the message was
 * sent (a file's name, a connection's app and account), so a rename or a removal later does not
 * change an old message.
 */
export const mentionViewSchema = z.strictObject({
  kind: mentionKindSchema,
  id: z.string().min(1),
  label: z.string().min(1).max(255),
});
export type MentionView = z.infer<typeof mentionViewSchema>;

export const messageViewSchema = z.strictObject({
  id: idSchema("message"),
  seq: seqSchema,
  at: instantSchema,
  status: messageStatusSchema,
  author: messageAuthorSchema,
  /** Display text. Empty while a message is still streaming; its text arrives as events. */
  text: z.string(),
  steps: z.array(stepViewSchema),
  /**
   * The notes the agent wrote in this turn, oldest first. Absent when it wrote none, as `files`
   * is: the web client validates every response against a strict schema, so an always-present key
   * would stop an older bundle opening any chat. Readers use `notes ?? []`.
   */
  notes: z.array(noteViewSchema).optional(),
  /**
   * The agent's checklist for this turn, as it stands now. Absent when it wrote none, as `notes`
   * is, so an older bundle still opens the chat. Readers use `plan`'s absence as none.
   */
  plan: planViewSchema.optional(),
  /**
   * What the agent read in the turn this message is, oldest first, for the answer's words (in this
   * message or a later one of the same run) to cite. Absent when it read nothing, as `notes` is,
   * for the same reason. Readers use `sources ?? []`.
   */
  sources: z.array(sourceViewSchema).optional(),
  /**
   * What the agent did with files and in the sandbox in this turn, oldest first. Absent when it
   * did nothing of the kind, as `notes` is, for the same reason. Readers use `work ?? []`.
   */
  work: z.array(workViewSchema).optional(),
  /**
   * When Abacor began writing code to run in this turn, while the turn is still being written: a
   * long piece of code takes the model a while, and until the call is whole it has no line of work.
   * Only the stream says it (`work.writing`, folded in from a snapshot's `inflight` too); a stored
   * row never carries it, and it goes when the turn ends, its line of work taking its place.
   */
  writing: instantSchema.optional(),
  /**
   * The batches Abacor landed in a process's draft in this turn, in the order they landed, for its
   * change card. Absent when it changed none, as `notes` is, for the same reason. Readers use
   * `processChanges ?? []`.
   */
  processChanges: z.array(processChangeViewSchema).optional(),
  /**
   * The tries Abacor started or ran again in this turn, in order, for the try card. Absent when it
   * started none, as `notes` is, for the same reason. Readers use `tries ?? []`.
   */
  tries: z.array(chatTryViewSchema).optional(),
  /**
   * What Abacor changed in the workspace's tables in this turn, one entry per table per batch, in
   * the order they landed, for the card's table lines. Absent when it changed none. Readers use
   * `tableChanges ?? []`.
   */
  tableChanges: z.array(tableChangeViewSchema).optional(),
  /**
   * The run an Abacor message was written in. Absent for a person's message, and for one the
   * reader cannot tie to a run. A continued run has a new ID, and no person's message before it,
   * so this is how the page tells where one run's reads end and the next begin.
   */
  runId: idSchema("run").optional(),
  /**
   * The run that began the request this Abacor message belongs to: the run a person's message
   * started, which runs triggered by an answer, a continue or a retry carry on (plan card spec,
   * section 4). A request has one plan card, so the page groups its blocks by this, never by its
   * own reading of the transcript. Absent where `runId` is, and from an older server.
   */
  requestRunId: idSchema("run").optional(),
  /** The request this message raised, shown as "Needs you". */
  requestId: z.string().min(1).optional(),
  /**
   * The files this message was sent with, or, for Abacor's message, the files it made and tells
   * of. Absent when it has none.
   */
  files: z.array(chatFileSchema).optional(),
  /**
   * What this message mentioned with `@`, in the order the person added them. Absent when it
   * mentioned nothing, as `files` is: an older bundle's strict schema would refuse an always-present
   * key, so ship the web client before the API starts sending one.
   */
  mentions: z.array(mentionViewSchema).optional(),
});
export type MessageView = z.infer<typeof messageViewSchema>;

/**
 * How a request is answered: in words, by picking one of the options it offers, or by filling one
 * field (decision `2026-11-03-an-ask-names-the-output-it-fills`). A field is named by `label` and
 * takes `field`'s shape. `fills` is the key of the step output the answer is recorded into, when the
 * question asks for one; `guess` is Abacor's own reading, shown filled in for the person to check.
 * A field response is new: an older bundle's strict schema refuses it, so ship the web client first.
 */
export const requestResponseSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("text") }),
  z.strictObject({
    type: z.literal("choice"),
    options: z.array(z.strictObject({ id: z.string().min(1), label: z.string().min(1) })).min(1),
  }),
  z.strictObject({
    type: z.literal("field"),
    label: z.string().min(1),
    field: fieldShapeSchema,
    fills: z.string().min(1).optional(),
    guess: fieldValueSchema.optional(),
  }),
]);
export type RequestResponse = z.infer<typeof requestResponseSchema>;

/**
 * Why a question is asked, in one sentence, and where that came from (decision
 * `2026-11-03-an-ask-says-why-and-the-person-decides`): a part of the playbook, which is the
 * firm's to change, or what one of the step's own look calls found in an app. The runtime resolves
 * the tag from what the model referred to; the model never writes it. Nothing here limits the
 * answer: the person decides.
 */
export const requestWhySchema = z.strictObject({
  text: z.string().min(1),
  from: z.discriminatedUnion("kind", [
    /** A step or a table of the playbook, by the name it has there. */
    z.strictObject({ kind: z.literal("playbook"), label: z.string().min(1) }),
    /** The app a look call of the step found the fact in. */
    z.strictObject({ kind: z.literal("found"), app: z.string().min(1) }),
  ]),
});
export type RequestWhy = z.infer<typeof requestWhySchema>;

export const requestViewSchema = z.strictObject({
  id: idSchema("humanRequest"),
  kind: requestKindSchema,
  /** Display text. */
  prompt: z.string(),
  /**
   * Why it is asked, for a question that said so. Absent otherwise: the schema is strict, so ship
   * the web client before the API sends it.
   */
  why: requestWhySchema.optional(),
  status: requestStatusSchema,
  deadlineAt: instantSchema.optional(),
  /** True only for the owner of the chat, and only for a kind answered in a chat. */
  canAnswer: z.boolean(),
  response: requestResponseSchema,
  /**
   * The approval an `approval` request wraps, for the owner of the chat only, so the page can ask
   * Connections what it will do. Absent otherwise, as `notes` is: an always-present key would stop
   * an older bundle opening any chat. A present one does too (the older schema is strict), so
   * ship the web client before the API starts sending it. Never the call's arguments, which the
   * chat never holds.
   */
  approvalId: idSchema("approval").optional(),
});
export type RequestView = z.infer<typeof requestViewSchema>;

/** A message waiting behind a run. Only the owner of a chat sees its queue. */
export const queuedViewSchema = z.strictObject({
  id: idSchema("inboxItem"),
  text: z.string(),
  delivery: deliverySchema,
  at: instantSchema,
});
export type QueuedView = z.infer<typeof queuedViewSchema>;

/** The most step targets one `draft.writing` names: an `edit_draft` call's most edits. */
export const DRAFT_WRITING_TARGETS_MAX = 50;

/**
 * A step an `edit_draft` call being written works on, read from its arguments as they stream:
 * the step it changes or removes, or the step an added one goes after (null: at the top). Named
 * only once its ID is whole. A hint of where Abacor works, never a change.
 */
export const draftTargetSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("change"), stepId: idSchema("processStep") }),
  z.strictObject({ kind: z.literal("remove"), stepId: idSchema("processStep") }),
  z.strictObject({ kind: z.literal("add"), after: idSchema("processStep").nullable() }),
]);
export type DraftTarget = z.infer<typeof draftTargetSchema>;

/**
 * Abacor is writing an `edit_draft` call: the call, and the steps it names so far in the order it
 * names them, the last being the one it is on. Until its batch lands or is refused.
 */
export const draftWritingSchema = z.strictObject({
  callId: toolCallIdSchema,
  at: instantSchema,
  targets: z.array(draftTargetSchema).max(DRAFT_WRITING_TARGETS_MAX),
});
export type DraftWriting = z.infer<typeof draftWritingSchema>;

export const runViewSchema = z.strictObject({
  id: idSchema("run"),
  status: runStatusSchema,
  /** A stop was asked for and the run has not yet ended. */
  stopping: z.boolean(),
  /**
   * Why a finished run failed: a catalog code, never text, and always there for a run that
   * failed (`INTERNAL_ERROR` when the run has no catalog code of its own). Absent for a run that
   * is waiting or working, and for one that answered, asked or was stopped.
   */
  errorCode: errorCodeSchema.optional(),
  /**
   * The run that began the request this run carries on (itself, for a run a person's message
   * started): what the rows it writes are stamped with. Absent from an older server.
   */
  requestRunId: idSchema("run").optional(),
  /**
   * The `edit_draft` call Abacor is writing in this run, until its batch lands or is refused. Only
   * the stream says it (`draft.writing`); a snapshot never does, so a page opened mid-edit shows
   * the batch when it lands.
   */
  drafting: draftWritingSchema.optional(),
});
export type RunView = z.infer<typeof runViewSchema>;

const peekLineSchema = z.strictObject({ text: z.string(), at: instantSchema });

/** A chat as the list shows it: no transcript, but the last thing each side said, for the peek. */
export const chatSummarySchema = z.strictObject({
  id: idSchema("thread"),
  title: z.string(),
  /** The web resolves this to a name from the member list it already has. */
  ownerUserId: userIdSchema,
  visibility: visibilitySchema,
  /** What a change of visibility must name (`visibilityInputSchema`): the revision seen here. */
  visibilityRevision: visibilityRevisionSchema,
  startedAt: instantSchema,
  lastAt: instantSchema,
  /** The last message's text, shortened (`previewOf`), until Abacor writes its own line. */
  preview: z.string(),
  /** A pending request names the caller. */
  needsYou: z.boolean(),
  /** When the owner archived the chat; null for a live one. Archived chats are left out of lists. */
  archivedAt: instantSchema.nullable(),
  /** How many files the chat holds: the references its messages made. */
  fileCount: z.int().min(0),
  /** Always null until processes exist. */
  process: z.null(),
  peek: z.strictObject({ ask: peekLineSchema.optional(), answer: peekLineSchema.optional() }),
});
export type ChatSummary = z.infer<typeof chatSummarySchema>;

// Stream events --------------------------------------------------------------------------------

/**
 * A custom event: AG-UI's escape hatch, one per thing a chat needs that AG-UI has no event for.
 * The name says which value follows.
 */
const customEvent = <const Name extends string, Value extends z.ZodType>(
  name: Name,
  value: Value,
) => z.strictObject({ type: z.literal("CUSTOM"), name: z.literal(name), value });

/**
 * How the owner's queue changed. An added message carries the whole item, since the stored event
 * names only its ID; a delivered one becomes a message, which arrives as its own
 * `message`.
 */
const queueChangeSchema = z.discriminatedUnion("change", [
  z.strictObject({ change: z.literal("added"), item: queuedViewSchema }),
  z.strictObject({ change: z.literal("withdrawn"), inboxItemId: idSchema("inboxItem") }),
  z.strictObject({ change: z.literal("delivered"), inboxItemId: idSchema("inboxItem") }),
]);
const requestRefSchema = z.strictObject({ requestId: idSchema("humanRequest") });

const customEventsSchema = z.discriminatedUnion("name", [
  /** A complete message, sent for a person's so a second tab or a viewer need not refetch. */
  customEvent("message", messageViewSchema),
  /** Owner only: a viewer never receives it. */
  customEvent("queue.changed", queueChangeSchema),
  customEvent("request.created", requestViewSchema),
  customEvent("request.answered", requestRefSchema),
  customEvent("request.expired", requestRefSchema),
  customEvent("request.canceled", requestRefSchema),
  /** The new value and the revision it made, which the next change must name. */
  customEvent(
    "visibility.changed",
    z.strictObject({ visibility: visibilitySchema, revision: visibilityRevisionSchema.min(1) }),
  ),
  customEvent("run.queued", z.strictObject({ runId: idSchema("run") })),
  /**
   * The run that just started carries on the request `requestRunId` began (itself, for a run a
   * person's message started): said right after `RUN_STARTED`, as an event of its own, so a page
   * built before it existed passes over it and still reads `RUN_STARTED` as it always did.
   */
  customEvent(
    "run.request",
    z.strictObject({ runId: idSchema("run"), requestRunId: idSchema("run") }),
  ),
  /** Abacor named the chat: the title it wrote replaces the first words the chat started with. */
  customEvent("title.changed", z.strictObject({ title: z.string().min(1) })),
  /** The agent wrote a note in a turn: the whole note, since the stored event names only its ID. */
  customEvent(
    "note.added",
    z.strictObject({ messageId: idSchema("message"), note: noteViewSchema }),
  ),
  /** The agent wrote or changed its plan: the whole plan, since the stored event names only the message. */
  customEvent(
    "plan.updated",
    z.strictObject({ messageId: idSchema("message"), plan: planViewSchema }),
  ),
  /** The run read something: the whole source, since the stored event names only its call. */
  customEvent(
    "source.added",
    z.strictObject({ messageId: idSchema("message"), source: sourceViewSchema }),
  ),
  /**
   * A file read or a code run began or came to an end: the whole line, which replaces any earlier
   * line with the same ID in the message (a call is told when it starts, and again when it ends).
   */
  customEvent("work.set", z.strictObject({ messageId: idSchema("message"), work: workViewSchema })),
  /**
   * Abacor changed a process's draft from this chat (decision
   * `2026-10-30-process-authored-from-the-chat`): one event per batch that landed, naming the
   * turn that made it, the process and draft, the batch (what Undo names), the revision it made
   * and the steps it touched, in order. It only draws the reply's change card; the draft's own
   * stream is what the canvas follows.
   */
  customEvent(
    "process.changed",
    z.strictObject({ messageId: idSchema("message"), ...processChangeViewSchema.shape }),
  ),
  /**
   * Abacor changed one of the workspace's tables from this chat (`edit_table`): one event per
   * table per batch that landed, naming the turn that made it. It draws the card's table lines.
   */
  customEvent(
    "table.changed",
    z.strictObject({ messageId: idSchema("message"), ...tableChangeViewSchema.shape }),
  ),
  /**
   * Abacor started a try of a playbook's draft, or ran one again, from this chat: one event per
   * call that did, naming the turn that made it. It draws the try card.
   */
  customEvent(
    "try.started",
    z.strictObject({ messageId: idSchema("message"), ...chatTryViewSchema.shape }),
  ),
  /**
   * Abacor began writing code to run, in the message being written: said as the model starts the
   * call, so a long piece of code shows as work under way instead of silence. Once per turn; the
   * turn's end (`TEXT_MESSAGE_END`) and its `work.set` lines take its place.
   */
  customEvent(
    "work.writing",
    z.strictObject({ messageId: idSchema("message"), at: instantSchema }),
  ),
  /**
   * Abacor is writing an `edit_draft` call: said as the call starts, with no targets, and again
   * each time its arguments name one more step. Each says the call's whole list so far.
   */
  customEvent("draft.writing", draftWritingSchema),
  /**
   * An `edit_draft` call's result came, landed or refused: the edit being written ends, if it is
   * that call's. Its own event, so `process.changed` keeps the shape a page built before it reads.
   */
  customEvent("draft.written", z.strictObject({ callId: toolCallIdSchema })),
]);
export const chatEventSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("RUN_STARTED"),
    runId: idSchema("run"),
    /** Which try at the run this is; a run taken over after its holder died starts again. */
    attempt: z.int().positive(),
  }),
  z.strictObject({
    type: z.literal("RUN_FINISHED"),
    runId: idSchema("run"),
    /** `interrupt`: the run stopped to wait for a person's answer. */
    outcome: z.enum(["success", "interrupt"]),
  }),
  z.strictObject({
    type: z.literal("RUN_ERROR"),
    runId: idSchema("run"),
    /** A catalog code, never text. */
    code: errorCodeSchema,
  }),
  z.strictObject({
    type: z.literal("TEXT_MESSAGE_START"),
    messageId: idSchema("message"),
    role: z.literal("assistant"),
  }),
  z.strictObject({
    type: z.literal("TEXT_MESSAGE_CONTENT"),
    messageId: idSchema("message"),
    delta: z.string(),
  }),
  z.strictObject({ type: z.literal("TEXT_MESSAGE_END"), messageId: idSchema("message") }),
  /**
   * Files Abacor made for the person, shown with the message that tells of them: its complete
   * list, sent after the message ends. A snapshot's message carries the same list in `files`.
   */
  z.strictObject({
    type: z.literal("MESSAGE_FILES"),
    messageId: idSchema("message"),
    files: z.array(chatFileSchema).min(1).max(FILES_PER_MESSAGE_MAX),
  }),
  z.strictObject({
    type: z.literal("TOOL_CALL_START"),
    toolCallId: toolCallIdSchema,
    /** The assistant message the step belongs to (AG-UI's name for it). */
    parentMessageId: idSchema("message"),
    step: stepViewSchema,
  }),
  z.strictObject({
    type: z.literal("TOOL_CALL_END"),
    toolCallId: toolCallIdSchema,
    /** How the step ended, or that it waits for an approval: anything but `running`. */
    status: stepStatusSchema.exclude(["running"]),
    /**
     * The message whose step this is, as `TOOL_CALL_START` names it. A tool call ID is unique
     * within one run only, so a step settled by a later run (an approval decided after another
     * run reused the ID) is found by its message. Absent from ends stored before it existed.
     */
    parentMessageId: idSchema("message").optional(),
  }),
  customEventsSchema,
]);
export type ChatEvent = z.infer<typeof chatEventSchema>;

/** An event and its place in the chat's log, which messages share. */
export const sequencedEventSchema = z.strictObject({ seq: seqSchema, event: chatEventSchema });
export type SequencedEvent = z.infer<typeof sequencedEventSchema>;

/**
 * What the stream sends. A batch covers everything after the client's cursor up to `lastSeq`
 * (events a viewer may not see are left out, so `lastSeq` can be past the last event). A reset
 * says the cursor is no use any more, and the client fetches the snapshot again.
 *
 * This is the strict shape the server writes and its tests check. A client reads with
 * `parseStreamFrame`, which keeps the events it knows when a newer server sends one it does not.
 */
export const streamFrameSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    v: z.literal(1),
    kind: z.literal("batch"),
    lastSeq: seqSchema,
    events: z.array(sequencedEventSchema),
  }),
  z.strictObject({ v: z.literal(1), kind: z.literal("reset") }),
]);
export type StreamFrame = z.infer<typeof streamFrameSchema>;

/**
 * A frame as a client first reads it: the envelope is checked and each event is left unread. The
 * envelope is versioned by `v` and is strict, so a shape this client does not know is a `v` bump
 * and not a surprise; the events inside it are what grow without one.
 */
export const streamEnvelopeSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    v: z.literal(1),
    kind: z.literal("batch"),
    lastSeq: seqSchema,
    events: z.array(z.strictObject({ seq: seqSchema, event: z.unknown() })),
  }),
  z.strictObject({ v: z.literal(1), kind: z.literal("reset") }),
]);
export type StreamEnvelope = z.infer<typeof streamEnvelopeSchema>;

/**
 * Reads a stream frame the way an older client must: an event type or shape it does not know is
 * dropped and counted, and the rest of the batch is kept in order. `lastSeq` is kept as sent, so
 * the cursor still moves past a dropped event. Null when the envelope itself is not one this
 * client understands (another `v`, an unknown `kind`, a malformed batch), which the caller treats
 * as a reason to fetch the snapshot again. Each kept event passed the strict schema, so strict
 * stays strict: a known type with an unexpected field is dropped like an unknown one.
 */
export function parseStreamFrame(input: unknown): { frame: StreamFrame; dropped: number } | null {
  const envelope = streamEnvelopeSchema.safeParse(input);
  if (!envelope.success) return null;
  if (envelope.data.kind === "reset") return { frame: { v: 1, kind: "reset" }, dropped: 0 };
  const events: SequencedEvent[] = [];
  for (const item of envelope.data.events) {
    const event = sequencedEventSchema.safeParse(item);
    if (event.success) events.push(event.data);
  }
  return {
    frame: { v: 1, kind: "batch", lastSeq: envelope.data.lastSeq, events },
    dropped: envelope.data.events.length - events.length,
  };
}

// Snapshot and search --------------------------------------------------------------------------

/** A whole chat as of `cursor`: the newest page of messages, and the message still being written. */
export const chatSnapshotSchema = z.strictObject({
  chat: chatSummarySchema,
  viewer: viewerSchema,
  /** The newest page, oldest first. */
  messages: z.array(messageViewSchema),
  hasEarlier: z.boolean(),
  /** Empty for a viewer. */
  queue: z.array(queuedViewSchema),
  run: runViewSchema.nullable(),
  requests: z.array(requestViewSchema),
  /** The stream events of the message being written now, so a refresh can pick up mid-sentence. */
  inflight: z.array(sequencedEventSchema),
  /** Where the stream resumes: events after it are not in this snapshot. */
  cursor: seqSchema,
});
export type ChatSnapshot = z.infer<typeof chatSnapshotSchema>;

export const searchHitSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("message"),
    id: idSchema("message"),
    at: instantSchema,
    author: messageAuthorSchema,
    text: z.string(),
    /** Abacor stopped and asked something in this message. */
    asked: z.boolean(),
  }),
  z.strictObject({
    kind: z.literal("step"),
    id: toolCallIdSchema,
    at: instantSchema,
    app: z.string(),
    text: z.string(),
  }),
]);
export type SearchHit = z.infer<typeof searchHitSchema>;

export const searchResultSchema = z.strictObject({
  chat: chatSummarySchema,
  /** The chat's own title matched, which shows even when no line inside it did. */
  titleMatch: z.boolean(),
  hits: z.array(searchHitSchema),
});
export type SearchResult = z.infer<typeof searchResultSchema>;

// Inputs ---------------------------------------------------------------------------------------

/**
 * `requestId` is the caller's key: sending the same one again answers what the first send did and
 * writes nothing. The text is a string of any length; the service trims it, refuses an empty one
 * as a validation error and a long one as `MESSAGE_TOO_LARGE`.
 */
/** The files a message is sent with: uploaded and `ready` already, at most `FILES_PER_MESSAGE_MAX`. */
const fileIdsSchema = z.array(idSchema("file")).max(FILES_PER_MESSAGE_MAX).default([]);

/** The things a message mentions with `@`: absent, not `[]`, when it mentions nothing. */
const mentionsSchema = z.array(mentionInputSchema).max(MENTIONS_PER_MESSAGE_MAX).optional();

export const createChatInputSchema = z.object({
  requestId: callerKeySchema,
  text: z.string(),
  fileIds: fileIdsSchema,
  mentions: mentionsSchema,
  /**
   * The playbook the chat is about, when it is started from that playbook's page: the chat points
   * at it from its first message, as a chat that built a playbook does, so Abacor reads and changes
   * that one (decision `2026-11-02-a-playbook-opens-on-its-overview-and-changing-docks-the-chat`).
   * One the caller cannot see is NOT_FOUND.
   */
  processId: idSchema("process").optional(),
});
export type CreateChatInput = z.infer<typeof createChatInputSchema>;

/**
 * `delivery` matters only when a run is already working: `steer` is read at the run's next turn,
 * `followUp` after it ends.
 */
export const sendInputSchema = z.object({
  requestId: callerKeySchema,
  text: z.string(),
  delivery: deliverySchema.default("followUp"),
  fileIds: fileIdsSchema,
  mentions: mentionsSchema,
});
export type SendInput = z.infer<typeof sendInputSchema>;

/**
 * `answerKey` makes a repeat of the same answer harmless. A choice may carry a `note`, words the
 * person added to the option they picked; the agent reads both. Blank is the same as none. The key
 * is optional so older callers, which never send one, still answer; a server that predates it drops
 * it (the schema is not strict), so deploy the API before the page that sends one.
 */
const givenAnswerSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), text: z.string() }),
  z.object({
    type: z.literal("choice"),
    optionId: z.string().min(1),
    note: z.string().optional(),
  }),
  /** A field's value, for a request that asks for one; checked against its shape. */
  z.object({ type: z.literal("value"), value: fieldValueSchema, note: z.string().optional() }),
  /** A ready file the person uploaded, for a request that asks for one; checked against its kind. */
  z.object({ type: z.literal("file"), fileId: idSchema("file"), note: z.string().optional() }),
]);

/** The longest note "Keep for next time" keeps: a How note's own limit. */
export const KEEP_TEXT_MAX = 1000;

/**
 * "Keep for next time" (decision `2026-11-03-help-is-one-loop-build-choices`): the words the
 * playbook keeps from this answer, as the person left them in the box. Only a question on a case
 * step's thread takes it.
 */
export const keepInputSchema = z.object({ text: z.string().trim().min(1).max(KEEP_TEXT_MAX) });
export type KeepInput = z.infer<typeof keepInputSchema>;

export const answerInputSchema = z.object({
  answerKey: callerKeySchema,
  answer: givenAnswerSchema,
  keep: keepInputSchema.optional(),
});
export type AnswerInput = z.infer<typeof answerInputSchema>;

/**
 * Where a kept answer went: into the draft a try was tried from (`draft`), or into the playbook's
 * open draft as a proposal (`proposal`), on the step that asked; or why it could not be kept. The
 * answer stands either way.
 */
export const keptViewSchema = z.union([
  z.strictObject({
    into: z.enum(["draft", "proposal"]),
    processId: idSchema("process"),
    draftId: idSchema("processDraft"),
    stepId: idSchema("processStep"),
    /** The step's place in the draft, from 1. */
    stepNumber: z.number().int().min(1),
  }),
  z.strictObject({ refusal: errorCodeSchema }),
]);
export type KeptView = z.infer<typeof keptViewSchema>;

/** Abacor's wording of a learning from an answer, for the person to edit; null when it has none. */
export const wordingInputSchema = z.object({ answer: givenAnswerSchema });
export type WordingInput = z.infer<typeof wordingInputSchema>;
export const wordingViewSchema = z.strictObject({ text: z.string().nullable() });
export type WordingView = z.infer<typeof wordingViewSchema>;

/**
 * `expectedRevision` is the chat's `visibilityRevision` as the caller last saw it, and is required:
 * the change is made only if no other was made since, or it is refused as a conflict, so a request
 * sent late cannot undo a newer decision about who may read the chat. A retry whose answer was lost
 * is a conflict too, since its own change moved the revision on; the caller reads the chat again
 * and finds it as it wanted.
 */
export const visibilityInputSchema = z.object({
  visibility: visibilitySchema,
  expectedRevision: visibilityRevisionSchema,
});
export type VisibilityInput = z.infer<typeof visibilityInputSchema>;

/**
 * Archiving puts a chat away: lists and searches leave it out unless they ask for it, and it takes
 * no new message until it is brought back. It changes who may read nothing, so it names no revision:
 * asking for what the chat already is changes nothing, and the last of two asks stands.
 */
export const archiveInputSchema = z.object({ archived: z.boolean() });
export type ArchiveInput = z.infer<typeof archiveInputSchema>;

/** Whether a list or a search leaves archived chats out (the default) or includes them. */
export const archivedFilterSchema = z.enum(["hide", "include"]);
export type ArchivedFilter = z.infer<typeof archivedFilterSchema>;

/**
 * A query string carries every value as text, so a whole number may arrive as "25". Anything that
 * is not plain digits is refused rather than guessed at. The contract's two message cursors read
 * theirs the same way.
 */
export const queryInteger = z.union([
  z.number(),
  z
    .string()
    .regex(/^\d{1,9}$/)
    .transform(Number),
]);

/** Nothing is clamped: a limit outside 1 to 50 is a validation error. */
export const listQuerySchema = z.object({
  scope: listScopeSchema.default("all"),
  /** Only chats this person owns. */
  by: userIdSchema.optional(),
  /** `include` lists archived chats among the rest; each says so in `archivedAt`. */
  archived: archivedFilterSchema.default("hide"),
  /** The `next` cursor of the previous page. */
  before: z.string().min(1).max(512).optional(),
  limit: queryInteger.pipe(z.int().min(1).max(LIST_MAX_LIMIT)).default(LIST_DEFAULT_LIMIT),
});
export type ListQuery = z.infer<typeof listQuerySchema>;

/**
 * Any string at all, including an empty one, full-text syntax and a very long one: the store makes
 * a search string safe, so the schema must not be the reason a search fails.
 */
export const searchQuerySchema = z.object({
  q: z.string(),
  archived: archivedFilterSchema.default("hide"),
});
export type SearchQuery = z.infer<typeof searchQuerySchema>;

/** Who may answer a request: in a chat, always its owner. */
export const responderSchema = z.object({ users: z.array(userIdSchema) });
export type Responder = z.infer<typeof responderSchema>;
